/* eslint-disable max-statements */
import { Address, PartialAddress } from '@dreryk/address-common-api';
import {
   MedicalDataAccessAuthorization,
   MedicalDataAccessAuthorizationUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';
import { FirstName, LastName, MiddleName } from '@dreryk/person-name-common-api';
import { AuthorizedPersonExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { createAuthorizedPersonTypeVisitor } from '../../../common/PersonForm/AuthorizedPersonIdentifier/AuthorizedPersonIdentifierTypeField';
import { patientConsentsAndAuthorizationsTranslationKeys } from '../../../translations';
import { MedicalDataAccessAuthorizationFormBean } from '../MedicalDataAccessAuthorizationModal/MedicalDataAccessAuthorizationModalForm/MedicalDataAccessAuthorizationModalForm.types';
import { useRefetchMedicalDataAccessAuthorization } from '../MedicalDataAccessAuthorizationPanel.hooks';

export const useMedicalDataAccessAuthorizationUpdateCommand = (
   id: MedicalDataAccessAuthorization['id']
): UseCqrsCommandResult<MedicalDataAccessAuthorization, MedicalDataAccessAuthorizationFormBean> => {
   const invalidateMedicalDataAccessAuthorization = useRefetchMedicalDataAccessAuthorization();

   return useCqrsCommand<MedicalDataAccessAuthorization, MedicalDataAccessAuthorizationFormBean>(
      (parameters) => {
         const command = new MedicalDataAccessAuthorizationUpdateCommand();
         command.medicalDataAccessAuthorizationId = id;
         command.firstName = FirstName.of(parameters.firstName!);
         if (parameters.middleName) {
            command.middleName = Choice.of(MiddleName.of(parameters.middleName));
         }
         command.lastName = LastName.of(parameters.lastName!);

         createAuthorizedPersonTypeVisitor(parameters.authorizedPersonIdentifierType)({
            visitPesel: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(Pesel.of(parameters.authorizedPersonIdentifierValue!))
               );
            },
            visitPassport: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(
                     Passport.of(
                        parameters.passportCountry!,
                        parameters.authorizedPersonIdentifierValue!,
                        parameters.passportValidTo!
                     )
                  )
               );
            },
            visitNationalIdentificationNumber: () => {
               command.externalIdentifier = Choice.of(
                  AuthorizedPersonExternalIdentifier.of(
                     NationalIdentificationNumber.of(
                        parameters.nationalIdentificationNumberCountry!,
                        parameters.authorizedPersonIdentifierValue!
                     )
                  )
               );
            },
            visitBirthDate: () => {
               command.birthDate = Choice.of(parameters.birthDate);
            },
         });

         command.address = Choice.of<Address>(
            PartialAddress.builder()
               .withCountry(parameters.country)
               .withPostcode(parameters.postcode)
               .withTown(parameters.town)
               .withStreet(parameters.street)
               .withBuilding(parameters.house)
               .withFlat(parameters.flat)
               .build()
         );

         command.phoneNumber = Choice.withUseDefaultIfNull(parameters.phoneNumber);
         command.forDocumentation = parameters.documentationAccess;
         command.forInformation = parameters.informationAccess;
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(
               patientConsentsAndAuthorizationsTranslationKeys.authorizations.authorizationModified
            );
            invalidateMedicalDataAccessAuthorization();
         },
      }
   );
};
